'use client';

import { BaseTranslations } from '../../../../baseTranslations';
import { RichText } from '../../../../components/RichText';
import { CmsProduct } from '../../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import { useCmsClientContext } from '../../../../utils/cmsPageContext';
import {
  formatAttributeItemValue,
  groupProductAttributes,
} from '../../../../utils/helpers';
import { getClientTranslations } from '../../../../utils/translations/getClientTranslations';
import { tr } from '../../../../utils/translations/tr';
import { ProductDetailsAttributeList } from '../../BaseComponents/ProductDetailsAttributeList';
import { ProductDetailsSectionWrapper } from '../../BaseComponents/ProductDetailsSectionWrapper';
import { EligibilitySectionHeader } from './EligibilitySectionHeader';

type ProductDetailsSectionEligibilityProps = {
  product?: CmsProduct;
  pushDataLayer?: PushDataLayerFn;
};

/**
 * Default eligibility (CC, PL and every vertical without its own): the number-typed eligibility
 * attributes (hardcoded key list in `groupProductAttributes`) plus the "Required documents"
 * rich-text accordion.
 */
export const ProductDetailsSectionEligibility: React.FC<
  ProductDetailsSectionEligibilityProps
> = ({ product, pushDataLayer }) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const { eligibility, requiredDocuments } = groupProductAttributes(product);

  return (
    <div
      className="flex flex-col gap-6"
      data-component="pdp-eligibility-default"
    >
      <EligibilitySectionHeader />
      {eligibility.length > 0 && (
        <ProductDetailsAttributeList
          attributes={eligibility}
          product={product}
          pushDataLayer={pushDataLayer}
        />
      )}
      {requiredDocuments.length > 0 && (
        <ProductDetailsSectionWrapper
          dropdownSection={true}
          title={tr('required_documents', staticText).toUpperCase()}
          sectionTitle={tr('pd_nav_eligibility', staticText)}
          product={product}
          pushDataLayer={pushDataLayer}
        >
          {requiredDocuments.map((document, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className="mhg-alpha-body-2">
              <RichText>
                {formatAttributeItemValue(document, false, true)}
              </RichText>
            </div>
          ))}
        </ProductDetailsSectionWrapper>
      )}
    </div>
  );
};
